import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { catchErrorLogRet, mapList } from '../../common/rest.operators';
import {
    OptionalColumn,
    OptionalColumnModelSetTransformer,
    validateOptionalColumns,
} from '../optional-column.insight.model';

import { CacheCriteriaParams } from './cache-criteria-params.pd.model';
import { CacheCriteria } from './cache-criteria.pd.model';
import { CacheDateParams } from './cache-date-params.pd.model';
import { CacheDateItem, cacheDateTransformer } from './cache-date.pd.model';
import { CacheMetricParams } from './cache-metric-params.pd.model';
import { CacheMetric } from './cache-metric.pd.model';
import {
    DataFilterOptions,
    DataFilterOptionsModelTransformer,
} from './data-filter-options.pd.model';
import {
    ExtMetricDataItem,
    MetricData,
    MetricModelSetTransformer,
    validateMetric,
} from './metric.pd.model';

@Injectable()
export class PerformanceDataService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    listCacheDates(pParams: CacheDateParams): Observable<CacheDateItem[]> {
        let params = new HttpParams();
        pParams.periodTypeIds?.forEach((e) => {
            params = params.append('periodTypeIds', String(e));
        });
        pParams.userIds?.forEach((e) => {
            params = params.append('userIds', String(e));
        });
        pParams.managerIds?.forEach((e) => {
            params = params.append('managerIds', String(e));
        });
        pParams.roleIds?.forEach((e) => {
            params = params.append('roleIds', String(e));
        });
        params = params.set('datesOnly', pParams.datesOnly ?? true);
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/cache-dates`,
                { params },
            )
            .pipe(map(cacheDateTransformer), catchErrorLogRet([]));
    }

    listCacheCriteria(
        pParams: CacheCriteriaParams,
    ): Observable<CacheCriteria[]> {
        let params = new HttpParams();
        if (pParams.dataViewTypeId != null) {
            params = params.append(
                'dataViewTypeId',
                String(pParams.dataViewTypeId),
            );
        }
        if (pParams.periodTypeId != null) {
            params = params.append(
                'periodTypeId',
                String(pParams.periodTypeId),
            );
        }
        if (pParams.eventDate != null) {
            params = params.append('eventDate', pParams.eventDate.toJSON());
        }
        pParams.roleIds?.forEach((e) => {
            params = params.append('roleIds', String(e));
        });
        pParams.userIds?.forEach((e) => {
            params = params.append('userIds', String(e));
        });
        pParams.siteIds?.forEach((e) => {
            params = params.append('siteIds', String(e));
        });
        if (pParams.criteriaTypeId != null) {
            params = params.append(
                'criteriaTypeId',
                String(pParams.criteriaTypeId),
            );
        }
        return this._http.get<CacheCriteria[]>(
            `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/cache-criteria`,
            { params },
        );
    }

    listCacheMetrics(pParams: CacheMetricParams): Observable<CacheMetric[]> {
        let params = new HttpParams();
        if (pParams.dataViewTypeId != null) {
            params = params.append(
                'dataViewTypeId',
                String(pParams.dataViewTypeId),
            );
        }
        if (pParams.periodTypeId != null) {
            params = params.append(
                'periodTypeId',
                String(pParams.periodTypeId),
            );
        }
        if (pParams.eventDate != null) {
            params = params.append('eventDate', pParams.eventDate.toJSON());
        }
        pParams.roleIds?.forEach((e) => {
            params = params.append('roleIds', String(e));
        });
        pParams.userIds?.forEach((e) => {
            params = params.append('userIds', String(e));
        });
        pParams.siteIds?.forEach((e) => {
            params = params.append('siteIds', String(e));
        });
        pParams.superGroupIds?.forEach((e) => {
            params = params.append('superGroupIds', String(e));
        });
        pParams.queueIds?.forEach((e) => {
            params = params.append('queueIds', String(e));
        });
        return this._http.get<CacheMetric[]>(
            `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/cache-metrics`,
            { params },
        );
    }

    listOptionalColumns(): Observable<OptionalColumn[]> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/optional-column-types`,
            )
            .pipe(
                mapList(new OptionalColumnModelSetTransformer()),
                tap((model) => validateOptionalColumns(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error(
                        'performancedata/optionalColumnTypes failed',
                    );
                }),
            );
    }

    listData(opts: DataFilterOptions): Observable<ExtMetricDataItem[]> {
        const params = new HttpParams()
            .set('viewType', String(opts.viewType))
            .set('periodType', String(opts.periodType));

        const transformed =
            new DataFilterOptionsModelTransformer().reverseTransform(opts);

        // TODO remove after old pi is removed
        if (!opts.outputCriteriaIdTypes && opts.outputCriteriaIdType != null) {
            opts.outputCriteriaIdTypes = [opts.outputCriteriaIdType];
        }

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/performance-data/data`,
                transformed,
                { params },
            )
            .pipe(mapList(), catchErrorLogRet([]));
    }

    listDataToMetricData(opts: DataFilterOptions): Observable<MetricData[]> {
        return this.listData(opts).pipe(
            map((model) => {
                return new MetricModelSetTransformer(
                    opts.displayNameCriteriaIdType,
                    opts.outputCriteriaIdTypes,
                ).transform(model);
            }),
            tap((model: MetricData[]) => validateMetric(model)),
            catchError((error) => {
                console.error(error);
                throw new Error(
                    'performancedata/data/:viewTypeId/:periodTypeId failed',
                );
            }),
        );
    }
}
